import { useState, useSyncExternalStore } from "react";

const FADE_PX = 40;

const AUTO_FADE_CLASS = {
    x: "scroll-fade-x-auto",
    y: "scroll-fade-y-auto",
} as const;
const EMPTY_FADE_STATE = { canScrollStart: false, canScrollEnd: false };

type FadeState = typeof EMPTY_FADE_STATE;

type Subscribe = (listener: () => void) => () => void;

function createFadeStore() {
    let snapshot = EMPTY_FADE_STATE;
    let subscribedKey: string | undefined;
    let subscribe: Subscribe = () => () => {};
    return {
        read: () => snapshot,
        write: (next: FadeState) => {
            snapshot = next;
        },
        subscriberFor: (key: string, create: () => Subscribe) => {
            if (key !== subscribedKey) {
                subscribedKey = key;
                subscribe = create();
            }
            return subscribe;
        },
    };
}

let supportsScrollTimeline: boolean | undefined;

function cssDrivesFade(): boolean {
    supportsScrollTimeline ??= CSS.supports("animation-timeline: scroll()");
    return (
        supportsScrollTimeline &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
}

export function useScrollFade(
    scrollRef: React.RefObject<HTMLElement | null>,
    direction: "x" | "y" = "x",

    resyncDeps: (string | number | boolean | null | undefined)[] = [],
    options: {
        fadePx?: number;
        edgeThreshold?: number;
        cssDriven?: boolean;
    } = {},
) {
    const [store] = useState(createFadeStore);
    const fadePx = options.fadePx ?? FADE_PX;
    const edgeThreshold = options.edgeThreshold ?? 1;
    const allowCssFade = options.cssDriven !== false;
    const subscriptionKey = [
        direction,
        fadePx,
        edgeThreshold,
        allowCssFade,
        ...resyncDeps.map(String),
    ].join("¦");

    const subscribe = store.subscriberFor(subscriptionKey, () => (listener) => {
        const el = scrollRef.current;
        if (!el) return () => {};

        const cssDriven = allowCssFade && cssDrivesFade();
        if (cssDriven) el.classList.add(AUTO_FADE_CLASS[direction]);

        const sync = () => {
            const scroll = direction === "x" ? el.scrollLeft : el.scrollTop;
            const client = direction === "x" ? el.clientWidth : el.clientHeight;
            const total = direction === "x" ? el.scrollWidth : el.scrollHeight;

            const start = scroll > edgeThreshold;
            const end = scroll < total - client - edgeThreshold;

            const current = store.read();
            if (
                start !== current.canScrollStart ||
                end !== current.canScrollEnd
            ) {
                store.write({ canScrollStart: start, canScrollEnd: end });
                listener();
            }

            if (cssDriven) return;
            el.style.setProperty(
                "--scroll-fade-start",
                start ? `${fadePx}px` : "0px",
            );
            el.style.setProperty(
                "--scroll-fade-end",
                end ? `${fadePx}px` : "0px",
            );
        };

        let frame: number | null = null;
        const update = () => {
            if (frame !== null) return;
            frame = requestAnimationFrame(() => {
                frame = null;
                sync();
            });
        };

        sync();

        el.addEventListener("scroll", update, { passive: true });
        const resizeObserver = new ResizeObserver(update);
        const observeScrollerAndChildren = () => {
            resizeObserver.disconnect();
            resizeObserver.observe(el);
            for (const child of el.children) resizeObserver.observe(child);
        };
        observeScrollerAndChildren();

        const mutationObserver = new MutationObserver(
            observeScrollerAndChildren,
        );
        mutationObserver.observe(el, { childList: true });

        return () => {
            el.classList.remove(AUTO_FADE_CLASS[direction]);
            el.removeEventListener("scroll", update);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            if (frame !== null) cancelAnimationFrame(frame);
        };
    });

    return useSyncExternalStore(subscribe, store.read, () => EMPTY_FADE_STATE);
}
